'use client';

import { useState } from 'react';
import { ChartNoAxesColumn, X } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { BaseModal } from './BaseModal';
import { Tooltip } from './Tooltip';
import type { DaemonTokenUsage } from '../lib/api';

const tokenFields = ['inputTokens', 'outputTokens', 'cacheReadInputTokens', 'cacheCreationInputTokens'] as const;

export function DaemonTriggerTokenUsage({ usage }: { usage: DaemonTokenUsage | null | undefined }) {
  const t = useTranslations('daemonTriggers.detail.tokenUsage');
  const tCommon = useTranslations('common');
  const locale = useLocale();
  const [isOpen, setIsOpen] = useState(false);
  const number = new Intl.NumberFormat(locale);
  const hasCounts =
    usage &&
    (usage.status === 'COMPLETE' || usage.status === 'PARTIAL') &&
    tokenFields.some((field) => usage[field] != null);
  const status = usage?.status ?? 'MISSING';

  return (
    <>
      <Tooltip
        content={hasCounts ? t('title') : `${t('title')}: ${t(`status.${status}`)}`}
        mode="overflowSafe"
        placement="top"
        className="inline-flex shrink-0"
      >
        <button
          type="button"
          className="btn btn-ghost btn-xs btn-square text-[var(--color-fg-muted)] hover:text-base-content"
          aria-label={t('title')}
          aria-haspopup="dialog"
          aria-expanded={Boolean(isOpen && hasCounts)}
          disabled={!hasCounts}
          onClick={() => setIsOpen(true)}
        >
          <ChartNoAxesColumn className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </Tooltip>
      <BaseModal isOpen={Boolean(isOpen && hasCounts)} onClose={() => setIsOpen(false)} size="sm" portal>
        <section aria-label={t('title')} className="space-y-3 p-4 text-sm">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold">{t('title')}</h3>
            <button
              type="button"
              className="btn btn-ghost btn-sm btn-square"
              aria-label={tCommon('actions.close')}
              onClick={() => setIsOpen(false)}
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
          <p className="text-[var(--color-fg-muted)]">{t(`status.${status}`)}</p>
          <dl className="space-y-1">
            {tokenFields.map((field) => (
              <div key={field} className="flex min-w-0 justify-between gap-4">
                <dt className="text-[var(--color-fg-subtle)]">{t(field)}</dt>
                <dd className="min-w-0 break-words text-right font-medium tabular-nums">
                  {usage?.[field] == null ? t('notCollected') : number.format(usage[field])}
                </dd>
              </div>
            ))}
          </dl>
          <p className="text-xs text-[var(--color-fg-muted)]">{t('scopeHint')}</p>
        </section>
      </BaseModal>
    </>
  );
}
